import type { Framework } from '@toeverything/infra';

import { AuthProvider } from '../provider/auth';
import { ServerScope } from '../scopes/server';
import request, { ContentType } from '../server/base';
import { apiVersion, getUserUrl, RESPONSE_CODE } from '../server/config';
import { signFn } from '../server/sign-server';
import { getCookie, getVisitorIdByCookie } from '../server/utils';
import { FetchService } from '../services/fetch';

export function configureDefaultAuthProvider(framework: Framework) {
  framework.scope(ServerScope).override(AuthProvider, resolver => {
    const fetchService = resolver.get(FetchService);
    return {
      async signInMagicLink(
        email: string,
        token: string,
        clientNonce?: string
      ) {
        await fetchService.fetch('/api/auth/magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, token, client_nonce: clientNonce }),
        });
      },

      async signInOauth(code: string, platform: string) {
        const res = await request({
          url: `${getUserUrl(apiVersion)}/${platform}/callback`,
          method: 'POST',
          data: { [platform + 'AuthCode']: code },
        });
        return res;
      },
      async signInPassword(credential: {
        email: string;
        password: string;
        verifyToken?: string;
        challenge?: string;
      }) {
        const headers: Record<string, string> = {};

        if (credential.verifyToken) {
          headers['x-captcha-token'] = credential.verifyToken;
        }
        if (credential.challenge) {
          headers['x-captcha-challenge'] = credential.challenge;
        }

        await fetchService.fetch('/api/auth/sign-in', {
          method: 'POST',
          body: JSON.stringify(credential),
          headers: {
            'content-type': 'application/json',
            ...headers,
          },
        });
      },
      async signOut(userId: string | number, token: string) {
        const visitorId = getVisitorIdByCookie();
        const config = {
          url: `${getUserUrl(apiVersion)}/sessions/${userId}`,
          method: 'DELETE',
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + token,
            'X-Yuanshi-DeviceId': visitorId,
            // userId: userId,
          },
        };

        return request(config);
      },
      async destoryAccount(userId: string | number, token: string) {
        const visitorId = getVisitorIdByCookie();
        const config = {
          url: `${getUserUrl(apiVersion)}/users/${userId}`,
          method: 'DELETE',
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + token,
            'X-Yuanshi-DeviceId': visitorId,
            // userId: userId,
          },
        };

        return request(config);
      },
      async checkUserHasRegistered(token: string) {
        const config = {
          url: `${getUserUrl(apiVersion)}/activation/status`,
          method: 'GET',
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + token,
          },
        };

        return request(config);
      },
      async signInOauthInvite(inviteCode: string, localStorageKey: string) {
        const token =
          JSON.parse(localStorage.getItem(localStorageKey) || '{}')?.auth
            ?.token || '';
        const res = await request({
          url: `${getUserUrl(apiVersion)}/activation/activate?invitationCode=${inviteCode}`,
          method: 'GET',
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + token,
          },
        });
        return res;
      },

      async getAuthInfoInServerSideV2(): Promise<{
        auth: any;
        isLogin: boolean;
        isRealLogin: boolean;
      }> {
        const loginToken = await getLoginTokenInServerSide();

        let auth = null;
        if (loginToken) {
          const userInfo = await getUserInfoInServerV2({
            token: loginToken,
          });
          if (userInfo) {
            auth = {
              token: loginToken,
              user: userInfo,
            };
          }
        }

        return {
          auth,
          isLogin: auth && auth.user && auth.token,
          isRealLogin: auth && auth.user && !auth.user.isTourist,
        };
      },

      async setUserAvatar(avatar: any) {
        const visitorId = getVisitorIdByCookie();
        const auth = getCookie('login')
          ? JSON.parse(decodeURIComponent(getCookie('login') || '') || '')
          : null;
        console.log('auth', auth);
        const config = {
          url: `${getUserUrl(apiVersion)}/users/${auth.user.id}/avatar`,
          method: 'PUT',
          data: { avatar },
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + auth?.token,
            'X-Yuanshi-DeviceId': visitorId,
            // userId: auth?.user?.id,
            'Content-Type': ContentType.upload,
          },
        };

        try {
          return await request(config);
        } catch (e) {
          console.log(e);
          throw { status: 408 };
        }
      },

      async setUserInfo({ key, value }: { key: string; value: any }) {
        const visitorId = getVisitorIdByCookie();
        const auth = getCookie('login')
          ? JSON.parse(decodeURIComponent(getCookie('login') || '') || '')
          : null;
        const config = {
          url: `${getUserUrl(apiVersion)}/users/${auth.user.id}`,
          method: 'PUT',
          data: {
            [key]: value,
          },
          headers: {
            'X-Yuanshi-Authorization': 'Bearer ' + auth?.token,
            'X-Yuanshi-DeviceId': visitorId,
            // userId: auth?.user?.id,
          },
        };

        try {
          return await request(config);
        } catch (e) {
          console.log(e);
          throw { status: 408 };
        }
      },
      async getLoginCode(email: string) {
        try {
          const res = await request({
            url: `${getUserUrl(apiVersion)}/mail/codes `,
            method: 'POST',
            data: { to: email },
          });
          return res;
        } catch (e) {
          console.log(e);
          throw e;
        }
      },
      async signInEmail({ email, code }: { email: string; code: string }) {
        try {
          const res = await request({
            url: `${getUserUrl(apiVersion)}/email/callback`,
            method: 'POST',
            data: { email, code },
          });
          return res;
        } catch (e) {
          console.log(e);
        }
      },
    };
  });
}

async function getLoginTokenInServerSide() {
  const cookies = Object.fromEntries(
    document.cookie.split('; ').map(pair => pair.split('='))
  );
  const login = cookies['login'] || '';
  let auth = null;
  if (login) {
    try {
      auth = JSON.parse(decodeURIComponent(login));
    } catch (e) {
      console.log(e);
    }
  }

  const loginToken: string = cookies['loginToken'] || auth?.token || '';

  return loginToken;
}
async function getUserInfoInServerV2({
  token,
  // logAttrs,
}: {
  token: string;
  logAttrs?: object;
}) {
  // const url = `${getUserUrl(apiVersion)}/users/${userId}`;
  const url = `${getUserUrl(apiVersion)}/users/1`;
  const config = signFn({
    method: 'GET',
    headers: {
      'X-Yuanshi-Authorization': 'Bearer ' + (token || ''),
    },
  });
  try {
    const res = await request({
      url: url,
      ...config,
    });

    // let res = await fetch(url, config);
    // res = await res.json();

    if (res.code === RESPONSE_CODE.SUCCESS && res?.data) {
      console.log('getUserInfoInServerV2 res: ', res.data);

      return res.data;
    } else {
      console.error('GET_USERINFO_ERROR', res);
      return null;
    }
  } catch (e) {
    console.error('GET_USERINFO_ERROR', e);
    return null;
  }
}
